/* ConfigAuditAction.js */

"use strict";

const _ = require("lodash");
const ConfigAuditActionScreenComponent = require("./ConfigAuditActionScreenComponent");
const Registrations = require("../../../../../RegistrationNamespaces");

/**
 * @typedef {Object} ConfigAuditAction~WorkTarget
 * @property {"Device"}        entityType
 * @property {"entity"}        category
 * @property {"query"|"multi"} method
 * @property {Object}          [query]
 * @property {string[]}        [ids]
 *
 * @typedef {{
 *          auditTitle:          {string},
 *          policyId:            {string},
 *          captureNewSnapShots: {boolean},
 *          [snapshotName]:      {string}
 * }} ConfigAuditAction~WorkInput
 *
 */

class ConfigAuditAction {
    constructor(logger, gameEditor, modalUserFeedback, DeviceEntity, ConfigAuditPolicy, userSession, activityMonitor, activeView) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._DeviceEntity = DeviceEntity;
        this._ConfigAuditPolicy = ConfigAuditPolicy;
        this._modalUserFeedback = modalUserFeedback;
        this._activeView = activeView;
        this._activityMonitor = activityMonitor;

        const device = _.invoke(this._gameEditor, "getDevice");
        this._deviceId = device && device.getId();
        this._orgId = userSession.activeOrganization.id;

        const showDeviceSelection = !this._deviceId;
        this.auditScreenComponent = new ConfigAuditActionScreenComponent(
            showDeviceSelection,
            modalUserFeedback,
            this._getPolicyLabels.bind(this),
            this._logger
        );

        this.run = this.run.bind(this);
    }

    run() {
        return this.auditScreenComponent
            .showTheView()
            .then((data) => {
                if (!(data.policy && data.policy.id)) {
                    return Promise.reject({
                        errorMsg: "Required Audit Policy not selected.",
                        errorCode: "ValidationError"
                    });
                }
                return data.schedule ? this.scheduleConfigAuditWork(data) : this.startConfigAuditWork(data);
            })
            .catch((err) => {
                if (err && err.userRejected) {
                    this._logger.warn({ error: err }, "ConfigAuditAction.run() - User Canceled");
                    return this._modalUserFeedback.hideDisplayed();
                } else {
                    this._logger.warn({ error: err }, "ConfigAuditAction.run() - Error");
                    return this._modalUserFeedback.displayError(err);
                }
            });
    }

    scheduleConfigAuditWork(data) {
        const schedule = data.schedule;
        const workTarget = this._getWorkTarget(data);
        const workInput = this._getWorkInput(data);

        let name = workInput.auditTitle;

        Object.assign(workInput, {
            notifyMe: data.notifyMe
        });
        // backdoor to allow every minute schedules for internal testing
        if (_.get(schedule, "every.hour") && name === "everymin") {
            _.set(schedule, "every.minute", schedule.every.hour);
            delete schedule.every.hour;
        }

        let description = "Policy: " + data.policy.name;
        let displayName = name ? name.trim() : "Audit Run - " + new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString();
        workInput.description = displayName;
        return this._DeviceEntity
            .scheduleAuditWork(this._userSession.activeOrganization.id, displayName, description, schedule, workTarget, workInput)
            .catch((error) => this._logger({ error }));
    }

    startConfigAuditWork(data) {
        const workTarget = this._getWorkTarget(data);
        const workInput = this._getWorkInput(data);

        /**
         *
         * @param {Object} progress - progress data object
         * @param {number} progress.startTime
         * @param {number} progress.targetCount
         * @param {number} progress.successCount
         * @param {number} progress.failureCount
         * @param {string} progress.policyId
         * @param {string} progress.policyName
         * @param {string} progress.policyDescription
         */
        const progressCB = ({ successCount, failureCount, targetCount }) => {
            if (this._modalUserFeedback) {
                this._modalUserFeedback.showLoadingProgress(`Succeeded ${successCount}, failed ${failureCount} of ${targetCount} devices`);
            }
        };

        this._modalUserFeedback.displayLoading({
            message: "Auditing your devices... This could take a moment",
            notDismissable: false
        });

        return this._ConfigAuditPolicy
            .runAuditWork(this._orgId, workTarget, workInput, { launchAndForget: false }, progressCB)
            .then((result) => this._showAuditResults(result));
    }

    _showAuditResults(result) {
        let userNotificationMessage = "";
        if (!result.summary) {
            userNotificationMessage = `Total number of devices: ${result._trimmed.targetResults || 0}`;
        } else {
            const { deviceCount, deviceSuccessCount, deviceErrorCount, devicesInViolationCount, violationCount, devicesSkippedCount } =
                result.summary;
            userNotificationMessage = [
                `Total devices: ${deviceCount || 0}`,
                `Audited devices: ${deviceSuccessCount || 0}`,
                `Skipped Devices:${devicesSkippedCount}` || 0,
                `Device errors: ${deviceErrorCount || 0}`,
                "",
                `Devices in violation: ${devicesInViolationCount || 0}`,
                `Total violations: ${violationCount || 0}`
            ].join("\r\n");
        }

        return this._gameEditor.launchAuditEditor
            ? this._showResultConfirmation(userNotificationMessage, result)
            : this._showResultNotification(userNotificationMessage);
    }

    _showResultNotification(userNotificationMessage) {
        return this._modalUserFeedback.displayNotification(userNotificationMessage, {
            title: "Audit Completed"
        });
    }

    _showResultConfirmation(userNotificationMessage, result) {
        return this._modalUserFeedback
            .displayConfirmation(userNotificationMessage, {
                title: "Audit Completed",
                confirmDetails: {
                    text: "View Results",
                    type: "additive"
                },
                rejectDetails: {
                    text: "OK",
                    type: "text"
                }
            })
            .then(() => {
                if (result.summary && result.summary.policyId) {
                    return this._gameEditor.launchAuditEditor(
                        {
                            fqn: Registrations.AUDIT_POLICY_EDITOR_VIEW_RESULTS
                        },
                        {
                            id: result.summary.policyId,
                            title: _.get(result.summary, "policyName", "")
                        }
                    );
                }
            });
    }

    _getPolicyLabels() {
        return this._ConfigAuditPolicy.loadLabelValues(this._orgId).then((labelValues) => {
            this.data = labelValues;
            return labelValues;
        });
    }

    _getWorkTarget(data) {
        const workTarget = {
            entityType: "Device",
            category: "entity"
        };
        switch (data.selection) {
            case "all":
                Object.assign(workTarget, {
                    method: "query",
                    query: {}
                });
                break;
            case "apply_current_filter":
                Object.assign(workTarget, {
                    method: "query",
                    query: !_.isEmpty(_.get(this._activeView._userGridData.getBasicSearchData(), "filterQuery"))
                        ? _.get(this._activeView._userGridData.getBasicSearchData(), "filterQuery")
                        : this._activeView._userGridData.getAdvancedSearchData()
                });
                break;
            case "selection":
                Object.assign(workTarget, {
                    method: "multi",
                    ids: this._activeView.getSelectedDeviceIds().selectedRows
                });
                break;
            default:
                Object.assign(workTarget, {
                    method: "multi",
                    ids: this._deviceId ? [this._deviceId] : []
                });
        }
        return workTarget;
    }

    _getWorkInput(data) {
        const snapshotName = data.captureNewSnapShots ? data.snapshotName : undefined;
        return {
            auditTitle: data.auditTitle,
            policyId: data.policy.id,
            captureNewSnapShots: data.captureNewSnapShots,
            snapshotName
        };
    }

    shutdown() {
        return Promise.resolve()
            .then(() => this.auditScreenComponent.shutdown())
            .then(() => Object.getOwnPropertyNames(this).forEach((prop) => (this[prop] = null)))
            .catch((error) => this._logger.warn({ error }, `${this.constructor.name}  shutdown Error`));
    }
}

module.exports = ConfigAuditAction;

ConfigAuditAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "$teve.entities.Device",
    "$teve.entities.ConfigAuditPolicy",
    "$teve.userSession",
    "$teve.activityMonitor",
    "gameEditor.activeView"
];

ConfigAuditAction.$displayDetails = {
    type: "Action",
    name: "ConfigAuditAction",
    icon: "configdrift.run_audit",
    shown: true,
    title: "Audit Configuration",
    order: 1
};
